import classNames from "classnames";
import { range } from "lodash";
import { forwardRef } from "react";
import { CollapsibleState } from "~/components/build/Show/pages/steps/list/components/TreeItem/CollapsibleState";
import { FlatTreeNode } from "~/components/build/Show/pages/steps/list/components/useTreeNodes";

// Vertical guide line positions — aligned with parent icon center at each depth.
// depth 0 icon center: pl-1.5 (0.375rem) + arrow w-7 (1.75rem) + half icon w-6 (0.75rem) = 2.875rem
// depth 1 icon center: pl-[2.125rem] + 1.75rem + 0.75rem = 4.625rem
const GUIDE_LINE_LEFT: Record<number, string> = {
  1: "2.875rem",
  2: "4.625rem",
};

// Horizontal connector endpoint — start of icon column at each depth.
// depth 1: pl-[2.125rem] + arrow w-7 (1.75rem) = 3.875rem
// depth 2: pl-[3.875rem] + 1.75rem = 5.625rem
const CONNECTOR_RIGHT: Record<number, string> = {
  1: "3.875rem",
  2: "5.625rem",
};

interface TreeItemProps extends React.HTMLAttributes<HTMLDivElement> {
  node: FlatTreeNode;
  depth: number;
  children: React.ReactNode;
  isLastChild: boolean;
  isSticky?: boolean;
}

/**
 * Tree item wrapper with ARIA treeitem attributes and visual guide
 * lines / connectors for the step tree hierarchy.
 */
export const TreeItem = forwardRef<HTMLDivElement, TreeItemProps>(
  ({ node, depth, isLastChild, isSticky, children, ...rest }, ref) => {
    const isExpanded =
      node.collapsibleState === CollapsibleState.Expanded ||
      node.collapsibleState === CollapsibleState.Full;

    return (
      <div
        ref={ref}
        role="treeitem"
        aria-level={depth < 0 ? 0 : depth}
        aria-expanded={node.collapsibleState !== CollapsibleState.None ? isExpanded : undefined}
        aria-posinset={node.index + 1}
        aria-setsize={undefined}
        aria-hidden={node.ariaHidden || undefined}
        tabIndex={-1}
        className={classNames("group relative flex w-full items-baseline bg-white")}
        {...rest}
      >
        {children}

        {/* Vertical guide lines for ancestor depths (always full height) */}
        {depth > 0 &&
          range(1, depth).map((level) => (
            <div
              key={`v-${level}`}
              className="absolute bottom-0 top-0 -z-10 border-l border-gray-400"
              style={{ left: GUIDE_LINE_LEFT[level] }}
            />
          ))}

        {/* Current depth connector — vertical line + horizontal branch */}
        {depth > 0 && (
          <>
            {/* Vertical line: full height for non-last, top-half only for last child */}
            <div
              className={classNames("absolute -z-10 border-l border-gray-400", {
                "bottom-0 top-0": !isLastChild,
                "top-0": isLastChild,
              })}
              style={{
                left: GUIDE_LINE_LEFT[depth],
                ...(isLastChild ? { height: "50%" } : {}),
              }}
            />
            {/* Horizontal branch from guide line to icon */}
            <div
              className="absolute -z-10 border-b border-gray-400"
              style={{
                left: GUIDE_LINE_LEFT[depth],
                width: `calc(${CONNECTOR_RIGHT[depth]} - ${GUIDE_LINE_LEFT[depth]})`,
                top: "50%",
              }}
            />
          </>
        )}
      </div>
    );
  },
);
